// شاشات كلمة المرور الثلاث — كانت غائبة كلها، والخادم يرسل رابط `/reset-password?token=…` إلى
// كل مستخدم يدعوه الأدمن. بلا هذه الشاشات لا يستطيع أحد غير المالك الدخول أصلًا.
//
// لا منطق أمني هنا: الرمز يُقرأ من العنوان ويُرسل كما هو، وسياسة كلمة المرور يفرضها الخادم
// (10 أحرف على الأقل، حروف وأرقام) ويُعرض رده. والرمز لا يُخزَّن ولا يُسجَّل ولا يُعاد عرضه.
import { useState, type FormEvent } from 'react';
import { ApiError, post } from '../lib/api.ts';
import { useSession } from './session.tsx';
import { Alert, Field, TextInput } from '../components/ui.tsx';
import { Logo } from '../components/Logo.tsx';

function Shell({ title, hint, children, label }: { title: string; hint?: string; children: React.ReactNode; label: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="card w-full max-w-sm space-y-4 p-6" role="region" aria-label={label}>
        <div className="flex flex-col items-center gap-2">
          <Logo priority className="h-20 sm:h-24" />
          <h1 className="text-center text-xl font-bold text-brand-700">{title}</h1>
          {hint && <p className="text-center text-sm text-ink-500">{hint}</p>}
        </div>
        {children}
      </div>
    </main>
  );
}

const PW_HINT = '10 أحرف على الأقل، وتحتوي حروفًا وأرقامًا';

/** يقرأ الرمز من العنوان الذي وصل بالبريد. لا يُكتب في التخزين ولا في السجل. */
export function tokenFromLocation(): string {
  try { return new URLSearchParams(window.location.search).get('token') ?? ''; } catch { return ''; }
}

/** «نسيت كلمة المرور»: بريد → طلب رابط. الرد واحد دائمًا — الخادم لا يكشف وجود الحساب. */
export function ForgotPasswordPage({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy(true);
    try {
      const r = await post<{ message: string }>('/auth/forgot-password', { email: email.trim() });
      setDone(r?.message ?? 'إذا كان البريد أو الرقم مسجلًا ستصلك رسالة على بريدك لإعادة التعيين');
    } catch (err) { setError(err instanceof ApiError ? err.message : 'تعذّر إرسال الطلب'); }
    finally { setBusy(false); }
  }

  return (
    <Shell title="نسيت كلمة المرور" hint="أدخل بريدك المسجَّل وسنرسل رابط إعادة التعيين" label="نسيت كلمة المرور">
      {done ? (
        <div className="space-y-2">
          <Alert kind="success">{done}</Alert>
          {/* البريد لا يصل؟ الأدمن يُخطَر ويبعت الرابط واتساب (27/09/2026) — فلا ينتظر الموظف بلا نهاية */}
          <p className="text-sm text-ink-500">الإيميل ما وصلش؟ كلّم المدير — هيبعتلك الرابط على واتساب.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" aria-label="طلب إعادة تعيين كلمة المرور">
          {error && <Alert>{error}</Alert>}
          <Field label="البريد الإلكتروني أو رقم التليفون" required>
            <TextInput type="text" name="email" autoComplete="username" inputMode="email" dir="ltr" required value={email} onChange={e => setEmail(e.target.value)} />
          </Field>
          <button type="submit" className="btn-primary w-full" disabled={busy}>{busy ? 'جارٍ الإرسال…' : 'إرسال رابط إعادة التعيين'}</button>
        </form>
      )}
      <button type="button" className="btn-ghost w-full" onClick={onBack}>العودة إلى تسجيل الدخول</button>
    </Shell>
  );
}

/** «تعيين كلمة المرور» من رابط البريد — للدعوة الأولى ولإعادة التعيين على السواء. */
export function ResetPasswordPage({ token, onDone }: { token: string; onDone: () => void }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault(); setError(null);
    if (pw !== pw2) { setError('كلمتا المرور غير متطابقتين'); return; }
    setBusy(true);
    try { await post('/auth/reset-password', { token, newPassword: pw }); setOk(true); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'تعذّر تعيين كلمة المرور'); }
    finally { setBusy(false); }
  }

  if (!token) {
    return (
      <Shell title="تعيين كلمة المرور" label="تعيين كلمة المرور">
        <Alert>الرابط ناقص أو غير صالح — اطلب رابطًا جديدًا من «نسيت كلمة المرور».</Alert>
        <button type="button" className="btn-ghost w-full" onClick={onDone}>العودة إلى تسجيل الدخول</button>
      </Shell>
    );
  }
  if (ok) {
    return (
      <Shell title="تعيين كلمة المرور" label="تعيين كلمة المرور">
        <Alert kind="success">تم تعيين كلمة المرور. سجّل الدخول بها الآن.</Alert>
        <button type="button" className="btn-primary w-full" onClick={onDone}>الذهاب إلى تسجيل الدخول</button>
      </Shell>
    );
  }
  return (
    <Shell title="تعيين كلمة المرور" hint={PW_HINT} label="تعيين كلمة المرور">
      <form onSubmit={onSubmit} className="space-y-4" aria-label="تعيين كلمة المرور">
        {error && <Alert>{error}</Alert>}
        <Field label="كلمة المرور الجديدة" required>
          <TextInput type="password" name="newPassword" autoComplete="new-password" required minLength={10} value={pw} onChange={e => setPw(e.target.value)} />
        </Field>
        <Field label="تأكيد كلمة المرور" required>
          <TextInput type="password" name="confirmPassword" autoComplete="new-password" required minLength={10} value={pw2} onChange={e => setPw2(e.target.value)} />
        </Field>
        <button type="submit" className="btn-primary w-full" disabled={busy}>{busy ? 'جارٍ الحفظ…' : 'حفظ كلمة المرور'}</button>
      </form>
    </Shell>
  );
}

/**
 * «تغيير كلمة المرور» — تظهر إلزاميًا حين يضع الأدمن `must_change_password`: الخادم يرفض كل
 * مسار آخر بـ403 حتى تُغيَّر، فلا بديل عن هذه الشاشة. تُستعمل اختياريًا من رأس الصفحة أيضًا.
 */
export function ChangePasswordPage({ forced, onDone }: { forced: boolean; onDone: () => void }) {
  const { signOut } = useSession();
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault(); setError(null);
    if (pw !== pw2) { setError('كلمتا المرور غير متطابقتين'); return; }
    setBusy(true);
    try { await post('/auth/change-password', { currentPassword: cur, newPassword: pw }); onDone(); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'تعذّر تغيير كلمة المرور'); }
    finally { setBusy(false); }
  }

  return (
    <Shell title="تغيير كلمة المرور" label="تغيير كلمة المرور"
      hint={forced ? 'يجب تغيير كلمة المرور قبل المتابعة' : PW_HINT}>
      <form onSubmit={onSubmit} className="space-y-4" aria-label="تغيير كلمة المرور">
        {forced && <Alert kind="info">طلب الأدمن تغيير كلمة المرور. لن تُتاح أي شاشة أخرى قبل ذلك.</Alert>}
        {error && <Alert>{error}</Alert>}
        <Field label="كلمة المرور الحالية" required>
          <TextInput type="password" name="currentPassword" autoComplete="current-password" required value={cur} onChange={e => setCur(e.target.value)} />
        </Field>
        <Field label="كلمة المرور الجديدة" required hint={PW_HINT}>
          <TextInput type="password" name="newPassword" autoComplete="new-password" required minLength={10} value={pw} onChange={e => setPw(e.target.value)} />
        </Field>
        <Field label="تأكيد كلمة المرور" required>
          <TextInput type="password" name="confirmPassword" autoComplete="new-password" required minLength={10} value={pw2} onChange={e => setPw2(e.target.value)} />
        </Field>
        <button type="submit" className="btn-primary w-full" disabled={busy}>{busy ? 'جارٍ الحفظ…' : 'حفظ كلمة المرور'}</button>
        <button type="button" className="btn-ghost w-full" onClick={() => (forced ? void signOut() : onDone())}>
          {forced ? 'تسجيل الخروج' : 'إلغاء'}
        </button>
      </form>
    </Shell>
  );
}
